import {
  Alert,
  MenuItem,
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  styled,
} from "@mui/material";

import { Fragment, useCallback, useMemo, useState, useEffect } from "react";

import { ArrowBackIos, ArrowForwardIos, DataUsage } from "@mui/icons-material";
import dayjs from "dayjs";
import ApiService, { PlantUtilizationData } from "@/api/ApiService";
import { usePersistantStore } from "@/stores/global";
import { useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { DataGridPro, GridColDef, GridRowClassNameParams } from "@mui/x-data-grid-pro";

type WeekField = "d1" | "d2" | "d3" | "d4" | "d5" | "d6" | "d7";
type UtilizationGridRow = { id: number; path: string } & Record<WeekField, number>;

const getTreeDataPath = (row: { path: string }) => row.path.split("###");

const StyledDataGrid = styled(DataGridPro)(() => ({
  "& .auslastung_level1": {
    backgroundColor: "#fff",
    "&:hover": {
      backgroundColor: "hsl(227, 100%, 99%)",
    },
  },
  "& .auslastung_level2": {
    backgroundColor: "hsl(227, 100%, 98%)",
    "&:hover": {
      backgroundColor: "hsl(227, 100%, 97%)",
    },
  },
  "& .auslastung_level3": {
    backgroundColor: "hsl(227, 100%, 96%)",
    "&:hover": {
      backgroundColor: "hsl(227, 100%, 95%)",
    },
  },
  "& .auslastung_level4": {
    backgroundColor: "hsl(227, 100%, 94%)",
    "&:hover": {
      backgroundColor: "hsl(227, 100%, 93%)",
    },
  },
}));

export default function Auslastung({ initialPlant }: { initialPlant: number }) {
  const userId = usePersistantStore(s => s.userId);
  const [selectedPlant, setSelectedPlant] = useState(initialPlant);
  useEffect(() => setSelectedPlant(initialPlant), [initialPlant, userId]);
  const [open, setOpen] = useState(false);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const minDate = dayjs().startOf("isoWeek");
  const maxDate = minDate.add(8, "week");

  const [date, setDate] = useState(minDate.add(1, "week"));

  const weekStart = date.format("YYYY-MM-DD");
  const weekEnd = date.endOf("isoWeek").format("YYYY-MM-DD");

  const materialQuery = useQuery<PlantUtilizationData, AxiosResponse>({
    queryKey: ["getPlantUtilization", userId],
    queryFn: ApiService.getPlantUtilization(),
    enabled: open,
  });

  const columns: GridColDef[] = [
    {
      field: "d1",
      headerName: date.format("ddd, DD.MM."),
      type: "number",
      valueFormatter: (value?: number) => {
        if (value == null || value === 0) {
          return "";
        }
        return `${value.toLocaleString()} t`;
      },
    },
    {
      field: "d2",
      headerName: date.add(1, "day").format("ddd, DD.MM."),
      type: "number",
      valueFormatter: (value?: number) => {
        if (value == null || value === 0) {
          return "";
        }
        return `${value.toLocaleString()} t`;
      },
    },
    {
      field: "d3",
      headerName: date.add(2, "day").format("ddd, DD.MM."),
      type: "number",
      valueFormatter: (value?: number) => {
        if (value == null || value === 0) {
          return "";
        }
        return `${value.toLocaleString()} t`;
      },
    },
    {
      field: "d4",
      headerName: date.add(3, "day").format("ddd, DD.MM."),
      type: "number",
      valueFormatter: (value?: number) => {
        if (value == null || value === 0) {
          return "";
        }
        return `${value.toLocaleString()} t`;
      },
    },
    {
      field: "d5",
      headerName: date.add(4, "day").format("ddd, DD.MM."),
      type: "number",
      valueFormatter: (value?: number) => {
        if (value == null || value === 0) {
          return "";
        }
        return `${value.toLocaleString()} t`;
      },
    },
    {
      field: "d6",
      headerName: date.add(5, "day").format("ddd, DD.MM."),
      type: "number",
      valueFormatter: (value?: number) => {
        if (value == null || value === 0) {
          return "";
        }
        return `${value.toLocaleString()} t`;
      },
    },
    {
      field: "d7",
      headerName: date.add(6, "day").format("ddd, DD.MM."),
      type: "number",
      valueFormatter: (value?: number) => {
        if (value == null || value === 0) {
          return "";
        }
        return `${value.toLocaleString()} t`;
      },
    },
  ];

  const rows = useMemo(() => {
    if (!materialQuery.isSuccess || !materialQuery.data) return [];
    const out: Record<string, Omit<UtilizationGridRow, "id">> = {};
    const names = new Map<string, number>();
    for (const plant of materialQuery.data.plants) names.set(plant.name, (names.get(plant.name) ?? 0) + 1);
    for (const row of materialQuery.data.rows) {
      if (row.date < weekStart || row.date > weekEnd || selectedPlant !== 0 && row.plantId !== selectedPlant) continue;
      const { group, subgroup, article, amount } = row;
      const plant = (names.get(row.plant) ?? 0) > 1 ? `${row.plant} (Werk ${row.plantId})` : row.plant;
      const weekday = ("d" + (dayjs(row.date).day() || 7)) as WeekField;
      const layer1 = plant;
      const layer2 = plant + "###" + group;
      const layer3 = plant + "###" + group + "###" + subgroup;
      const layer4 = plant + "###" + group + "###" + subgroup + "###" + article;
      if (!(layer1 in out)) out[layer1] = { path: layer1, d1: 0, d2: 0, d3: 0, d4: 0, d5: 0, d6: 0, d7: 0 };
      if (!(layer2 in out)) out[layer2] = { path: layer2, d1: 0, d2: 0, d3: 0, d4: 0, d5: 0, d6: 0, d7: 0 };
      if (!(layer3 in out)) out[layer3] = { path: layer3, d1: 0, d2: 0, d3: 0, d4: 0, d5: 0, d6: 0, d7: 0 };
      if (!(layer4 in out)) out[layer4] = { path: layer4, d1: 0, d2: 0, d3: 0, d4: 0, d5: 0, d6: 0, d7: 0 };
      out[layer1][weekday] += amount;
      out[layer2][weekday] += amount;
      out[layer3][weekday] += amount;
      out[layer4][weekday] += amount;
    }
    return Object.values(out)
      .sort((a, b) => a.path.localeCompare(b.path))
      .map((x, id) => ({ ...x, id }));
  }, [materialQuery.isSuccess, materialQuery.data, weekEnd, weekStart, selectedPlant]);

  return (
    <Fragment>
      <Button startIcon={<DataUsage />} variant="outlined" size="small" onClick={() => setOpen(true)}>
        Auslastung
      </Button>
      {open ? (
        <Dialog open={open} maxWidth="xl" fullWidth onClose={close}>
          <Fragment>
            <DialogTitle
              sx={{
                p: 1,
                px: 2,
                background: "#002196",
                color: "white",
                borderBottom: "1px solid rgba(255,255,255,0.5)",
              }}
            >
              Auslastung
            </DialogTitle>
            <DialogContent
              sx={{
                p: 0,
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box sx={{ display: "flex", gap: "5px", p: "5px", borderBottom: "1px solid #aaa", alignItems: "center" }}>
                <IconButton
                  disabled={!!minDate && date.format("YYYY-MM-DD") <= minDate.format("YYYY-MM-DD")}
                  size="small"
                  onClick={() => setDate(date.subtract(1, "week"))}
                >
                  <ArrowBackIos fontSize="small" />
                </IconButton>

                <Box
                  sx={{
                    display: "flex",
                    minWidth: "145px",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    cursor: "pointer",
                  }}
                >
                  <Typography variant="h6">{date.format("[KW] W ([ab] DD.MM.YYYY)")}</Typography>
                </Box>
                <IconButton
                  disabled={!!maxDate && date.format("YYYY-MM-DD") >= maxDate.format("YYYY-MM-DD")}
                  size="small"
                  onClick={() => setDate(date.add(1, "week"))}
                >
                  <ArrowForwardIos fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ p: 1 }}>
                <TextField select label="Werk" size="small" fullWidth value={selectedPlant} disabled={!materialQuery.isSuccess}
                  onChange={event => setSelectedPlant(Number(event.target.value))}>
                  <MenuItem value={0}>Alle Werke</MenuItem>
                  {selectedPlant > 0 && !materialQuery.data?.plants.some(plant => plant.id === selectedPlant) ? <MenuItem value={selectedPlant}>Werk {selectedPlant}</MenuItem> : null}
                  {(materialQuery.data?.plants ?? []).map(plant => <MenuItem key={plant.id} value={plant.id}>{plant.name}{materialQuery.data?.plants.some(other => other.id !== plant.id && other.name === plant.name) ? ` (Werk ${plant.id})` : ""}</MenuItem>)}
                </TextField>
              </Box>
              {materialQuery.isError ? <Alert severity="error">Auslastung konnte nicht geladen werden.<Button onClick={() => materialQuery.refetch()}>Erneut versuchen</Button></Alert> : null}
              <StyledDataGrid
                showToolbar
                slotProps={{
                  toolbar: { csvOptions: { delimiter: ";", utf8WithBom: true } },
                }}
                groupingColDef={{
                  hideDescendantCount: true,
                }}
                loading={materialQuery.isLoading}
                disableRowSelectionOnClick
                getRowHeight={() => 35}
                treeData
                getTreeDataPath={getTreeDataPath}
                rows={rows}
                columns={columns}
                getRowClassName={(params: GridRowClassNameParams<UtilizationGridRow>) => {
                  console.log(params.row);
                  return "auslastung_level" + getTreeDataPath(params.row).length;
                }}
              />
            </DialogContent>
            <DialogActions sx={{ borderTop: "1px solid #aaa" }}>
              <Button onClick={close}>Schließen</Button>
            </DialogActions>
          </Fragment>
        </Dialog>
      ) : null}
    </Fragment>
  );
}
